import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, API } from "@/lib/api";
import { REPORTS } from "@/lib/reportsConfig";
import ReportTable from "@/components/ReportTable";
import AIDrawer from "@/components/AIDrawer";
import GstGlRecon from "@/components/GstGlRecon";
import DocLinesDialog from "@/components/DocLinesDialog";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { toast } from "sonner";

const pad2 = (n) => String(n).padStart(2, "0");
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-01`; };
const todayStr = () => { const d = new Date(); return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`; };

export default function ReportPage() {
  const { reportKey } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const config = REPORTS[reportKey];
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [tab, setTab] = useState("invoices");
  const [docDrill, setDocDrill] = useState(null);
  const [stmtParty, setStmtParty] = useState("");
  const [stmtBusy, setStmtBusy] = useState(false);
  const isLedger = reportKey === "ar-ledger" || reportKey === "ap-ledger";

  const parties = useMemo(() => {
    if (!isLedger || !data) return [];
    const m = new Map();
    (data.rows || []).forEach((r) => { if (r.party_code && !m.has(r.party_code)) m.set(r.party_code, r.party_name); });
    return [...m.entries()].map(([code, name]) => ({ code, name })).sort((a, b) => String(a.name).localeCompare(String(b.name)));
  }, [data, isLedger]);

  useEffect(() => {
    if (!isLedger || !initialQuery || !parties.length) return;
    const q = initialQuery.toLowerCase();
    const hit = parties.find((p) => String(p.name).toLowerCase().includes(q) || String(p.code).toLowerCase() === q);
    if (hit) setStmtParty(hit.code);
  }, [parties, initialQuery, isLedger]);

  const downloadStatement = async () => {
    if (!stmtParty) return;
    setStmtBusy(true);
    try {
      const res = await fetch(`${API}/reports/${reportKey}/statement?party=${encodeURIComponent(stmtParty)}`, { credentials: "include" });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        toast.error(j.detail || "Statement export failed");
        return;
      }
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DeePiping_Statement_${stmtParty}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
    } finally { setStmtBusy(false); }
  };

  useEffect(() => {
    setData(null);
    setError("");
    setTab("invoices");
    setDocDrill(null);
    const hasDf = !!REPORTS[reportKey]?.dateField;
    const f = hasDf ? monthStart() : "";
    const t = hasDf ? todayStr() : "";
    setFrom(f);
    setTo(t);
    api
      .get(`/reports/${reportKey}`)
      .then(({ data }) => {
        setData(data);
        if (hasDf) {
          const dfk = REPORTS[reportKey].dateField;
          const any = (data.rows || []).some((r) => {
            const d = (r[dfk] || "").slice(0, 10);
            return d >= f && d <= t;
          });
          if (!any) { setFrom(""); setTo(""); }  // nothing this month yet — show everything
        }
      })
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [reportKey]);

  const df = config?.dateField;
  const rows = useMemo(() => {
    if (!data) return [];
    if (!df || (!from && !to)) return data.rows;
    return data.rows.filter((r) => {
      const d = (r[df] || "").slice(0, 10);
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [data, df, from, to]);

  const totals = useMemo(() => {
    if (!data?.totals) return data?.totals;
    if (!df || (!from && !to)) return data.totals;
    const t = {};
    Object.keys(data.totals).forEach((k) => {
      t[k] = typeof data.totals[k] === "number" ? rows.reduce((s, r) => s + (Number(r[k]) || 0), 0) : data.totals[k];
    });
    return t;
  }, [data, rows, df, from, to]);

  if (!config) return <div className="text-slate-500">Unknown report.</div>;

  return (
    <div className="space-y-5" data-testid={`report-page-${reportKey}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{config.title}</h1>
          <p className="text-sm text-slate-500 mt-1 flex items-center gap-2">
            {config.subtitle}
            {data && <span className="text-slate-600"> · As of {data.as_of}</span>}
            {data && (
              <span data-testid={`${reportKey}-source-chip`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${data.data_source === "SAP" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-300"}`}>
                {data.data_source === "SAP" ? "SAP Live · Read-only" : "Mock Data"}
              </span>
            )}
          </p>
        </div>
        <AIDrawer reportKey={reportKey} reportTitle={config.title} />
      </div>
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>}
      {!data && !error && <div className="text-slate-500 text-sm animate-pulse">Loading report…</div>}
      {(reportKey === "gstr1" || reportKey === "gstr2") && (
        <div className="flex items-center gap-1.5" data-testid={`${reportKey}-tabs`}>
          {[["invoices", "Invoices"], ["recon", reportKey === "gstr2" ? "ITC GL Reconciliation" : "GL Reconciliation"]].map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`${reportKey}-tab-${k}`}
              className={`text-xs font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === k ? "bg-[#B01E24] text-white border-[#B01E24]" : "bg-white text-slate-600 border-slate-300 hover:border-[#B01E24]/50"}`}>
              {label}
            </button>
          ))}
        </div>
      )}
      {(reportKey === "gstr1" || reportKey === "gstr2") && tab === "recon" ? (
        <GstGlRecon side={reportKey === "gstr2" ? "input" : "output"} />
      ) : (
        <>
      {data && df && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${reportKey}-date-filters`}>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Invoice date</span>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid={`${reportKey}-date-from`}
            className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50" />
          <span className="text-slate-400 text-xs">to</span>
          <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid={`${reportKey}-date-to`}
            className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50" />
          {(from || to) && (
            <button onClick={() => { setFrom(""); setTo(""); }} data-testid={`${reportKey}-date-clear`}
              className="text-xs text-[#B01E24] border border-[#B01E24]/30 rounded-full px-3 py-1 hover:bg-[#B01E24]/5">
              Clear · {rows.length} of {data.rows.length} rows
            </button>
          )}
        </div>
      )}
      {isLedger && data && (
        <div className="flex flex-wrap items-center gap-2" data-testid={`${reportKey}-statement-bar`}>
          <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">Party statement</span>
          <select value={stmtParty} onChange={(e) => setStmtParty(e.target.value)} data-testid={`${reportKey}-statement-party-select`}
            className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-[#B01E24]/50 max-w-xs">
            <option value="">Select {reportKey === "ar-ledger" ? "customer" : "vendor"}…</option>
            {parties.map((p) => <option key={p.code} value={p.code}>{p.name} ({p.code})</option>)}
          </select>
          <button onClick={downloadStatement} disabled={!stmtParty || stmtBusy} data-testid={`${reportKey}-statement-button`}
            className="flex items-center gap-1.5 text-xs bg-emerald-700/80 hover:bg-emerald-600 disabled:opacity-50 text-white border border-emerald-600/50 rounded-md px-3 py-1.5 transition-colors">
            {stmtBusy ? <Loader2 size={12} className="animate-spin" /> : <FileSpreadsheet size={12} />} Statement Excel
          </button>
        </div>
      )}
      {data?.note && (
        <div className="text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded-md px-3 py-2" data-testid={`${reportKey}-note`}>{data.note}</div>
      )}
      {data && (
        <ReportTable
          key={`${reportKey}-${initialQuery}`}
          reportKey={reportKey}
          columns={config.columns}
          rows={rows}
          totals={totals}
          initialQuery={initialQuery}
          onRowClick={
            config.drilldown
              ? (row) => navigate(`${config.drilldown.to}?q=${encodeURIComponent(row[config.drilldown.param])}`)
              : isLedger
                ? (row) => setDocDrill(row)
                : undefined
          }
        />
      )}
      {docDrill && (
        <DocLinesDialog docNo={docDrill.doc_no} report={reportKey} onClose={() => setDocDrill(null)} />
      )}
        </>
      )}
    </div>
  );
}
